import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';

import { getEmptyADF } from '@atlaskit/adf-utils/empty-adf';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LinkButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import EmptyState from '@atlaskit/empty-state';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Text } from '@atlaskit/primitives/compiled';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import Spinner from '@atlaskit/spinner';
import { useThemeObserver } from '@atlaskit/tokens';
import {
	ContextualAnalyticsData,
	fireOperationalAnalytics,
	fireTrackAnalytics,
	fireUIAnalytics,
	MODAL,
} from '@atlassian/analytics-bridge';

import errorImage from '../assets/error.png';
import {
	JIRA_EMBED_ISSUE_CREATE_CLOSE,
	JIRA_EMBED_ISSUE_CREATE_FAILURE,
	JIRA_EMBED_ISSUE_CREATE_INIT,
	JIRA_EMBED_ISSUE_CREATE_READY,
	JIRA_EMBED_ISSUE_CREATE_SUCCESS,
} from '../common/constants';
import { AnalyticsProvider, debounce, getIframeSrc } from '../common/utils';
import { useGetDomainAndAvailableSites } from '../controllers/use-get-first-available-site-domain';

import { DEFAULT_TIMEOUT, MAX_ERROR_IMAGE_HEIGHT, MAX_ERROR_IMAGE_WIDTH } from './constants';
import { messages } from './messages';
import { type GicAnywhereProps, type GICFrameProps, type GICPayload } from './types';

const phases = {
	loading: 'loading',
	timeout: 'timeout',
	error: 'error',
	idle: 'idle',
} as const;

type GicAnywherePhases =
	| typeof phases.loading
	| typeof phases.timeout
	| typeof phases.error
	| typeof phases.idle;

export const GicAnywhere = (props: GicAnywhereProps): React.JSX.Element => {
	return (
		<AnalyticsProvider>
			<ContextualAnalyticsData sourceType={MODAL} sourceName="gicAnywhere">
				<GicAnywhereWithoutAnalytics {...props} />
			</ContextualAnalyticsData>
		</AnalyticsProvider>
	);
};

const styles = cssMap({
	hiddenDisplay: {
		display: 'none',
	},
	gicAnywhere: {
		width: '100%',
		height: '100%',
	},
	loadingScreen: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		textAlign: 'center',
	},
});

export const GicAnywhereWithoutAnalytics = ({
	cloudId,
	overrideCloudId,
	configuration,
	summary,
	assignee,
	descriptionAdf,
	consumer,
	timeoutMs = DEFAULT_TIMEOUT,
	onInit,
	onSuccess,
	onFailure,
	onClose,
	onTimeout,
	parentIssueId,
	projectId,
	issueTypeId,
	issueTypes,
	isPayloadReady = false,
	additionalMappedFields,
	baseUrlForAvailableSites,
}: GicAnywhereProps): React.JSX.Element => {
	const { availableSites, domainName, isOverrideSiteAvailable } = useGetDomainAndAvailableSites({
		cloudId,
		overrideCloudId,
		baseUrlForAvailableSites,
	});

	const displaySitePicker = availableSites && availableSites.length > 1;

	const [phase, setPhase] = useState<GicAnywherePhases>(phases.loading);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	useEffect(() => {
		const timeout = (() => {
			switch (phase) {
				case phases.loading:
					return setTimeout(() => {
						fireOperationalAnalytics(createAnalyticsEvent({}), 'globalIssueCreateEmbed timedOut', {
							consumer,
							timeoutMs,
						});
						setPhase(phases.timeout);
						onTimeout?.();
					}, timeoutMs);

				case phases.timeout:
					// if timeoutMs passes again after initial timeout, assume GIC is not loading and set to error state
					return setTimeout(() => {
						fireOperationalAnalytics(createAnalyticsEvent({}), 'globalIssueCreateEmbed error', {
							consumer,
							timeoutMs: timeoutMs * 2,
						});
						setPhase(phases.error);
						onFailure(new Error('GIC has timed out'));
					}, timeoutMs);
			}
		})();

		// set Timeout for loading, reset for any other state.
		return () => timeout && clearTimeout(timeout);
	}, [consumer, createAnalyticsEvent, onFailure, onTimeout, phase, timeoutMs]);

	// we mimic "external callback" for our needs
	const onGICReady = useCallback((): GICPayload => {
		setPhase(phases.idle);
		return {
			summary: summary ?? 'No summary provided',
			descriptionAdf: descriptionAdf || getEmptyADF(),
			projectId,
			parentIssueId,
			issueTypeId,
			issueTypes: issueTypes?.join(','),
			assignee,
			fieldData: additionalMappedFields,
		};
	}, [
		summary,
		descriptionAdf,
		projectId,
		parentIssueId,
		issueTypeId,
		issueTypes,
		assignee,
		additionalMappedFields,
	]);

	const renderPhaseInformation = useMemo(() => {
		switch (phase) {
			case phases.timeout:
			case phases.loading:
				return (
					<Box testId="gic-anywhere-loading" xcss={cx(styles.gicAnywhere, styles.loadingScreen)}>
						<Spinner size="large" />
						<Text>
							{formatMessage(
								phase === phases.timeout ? messages.iFrameSlowLoading : messages.iFrameLoading,
								{
									br: <br />,
								},
							)}
						</Text>
					</Box>
				);
			case phases.error:
				return (
					<Box testId="gic-anywhere-error" xcss={styles.gicAnywhere}>
						<EmptyState
							header={formatMessage(messages.errorHeader)}
							description={formatMessage(messages.errorBody)}
							primaryAction={
								<LinkButton
									onClick={() => {
										onClose();
									}}
									href={domainName || '/'}
									target="_blank"
									appearance="primary"
								>
									{formatMessage(messages.openInJiraButton)}
								</LinkButton>
							}
							imageUrl={errorImage}
							imageWidth={MAX_ERROR_IMAGE_WIDTH}
							maxImageWidth={MAX_ERROR_IMAGE_WIDTH}
							maxImageHeight={MAX_ERROR_IMAGE_HEIGHT}
						/>
					</Box>
				);
			default:
				return undefined;
		}
	}, [phase, formatMessage, domainName, onClose]);

	return (
		<>
			{renderPhaseInformation}
			<UFOLoadHold name="gic-anywhere-iframe-loading" hold={phase === phases.loading} />
			{/**
			 * Keep iframe hidden until we are ready to display it
			 */}
			<Box xcss={phase !== phases.idle ? styles.hiddenDisplay : styles.gicAnywhere}>
				{/* we cannot render iframe until domain is determined */}
				{domainName !== undefined && (
					<GICFrame
						domainName={domainName}
						consumer={consumer}
						onGICReady={onGICReady}
						onInit={onInit}
						onSuccess={onSuccess}
						onFailure={onFailure}
						onClose={onClose}
						configuration={configuration}
						displaySitePicker={displaySitePicker}
						isPayloadReady={isPayloadReady}
						overrideCloudId={isOverrideSiteAvailable ? overrideCloudId : undefined}
					/>
				)}
			</Box>
		</>
	);
};

export const GICFrame = ({
	onGICReady,
	consumer,
	domainName,
	overrideCloudId,
	onClose,
	onFailure,
	onSuccess,
	onInit,
	configuration,
	displaySitePicker = true,
	isPayloadReady = false,
}: GICFrameProps): React.JSX.Element => {
	di(IFrame);

	const iframeRef = useRef<HTMLIFrameElement>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { colorMode } = useThemeObserver();
	const [isCreateSuccess, setIsCreateSuccess] = useState(false);
	const [isGicReady, setIsGicReady] = useState<boolean>(false);
	const isGicReadyRef = useRef(isGicReady);

	// we will react on this event only once
	// we dont want to reattach handlers on every render
	const onGICReadyRef = useRef(onGICReady);
	onGICReadyRef.current = onGICReady;

	const params = useMemo(
		() => ({ product: consumer, actionSource: 'issue-create-anywhere-embed' }),
		[consumer],
	);

	// Create a debounced version of onInit that will only execute the latest call after 2 seconds
	const debouncedOnInit: (() => void) | undefined = useMemo(() => {
		if (!onInit) {
			return undefined;
		}

		if (!fg('jira_ai_fix_aiwc_duplicated_analytics')) {
			return onInit;
		}

		return debounce(() => onInit(), 2000);
	}, [onInit]);

	// Cleanup the debounced function on unmount
	useEffect(() => {
		return () => {
			const debouncedFn = debouncedOnInit as { cancel?: () => void };
			// Only call cancel if debouncedOnInit is actually a debounced function
			if (debouncedFn && typeof debouncedFn.cancel === 'function') {
				debouncedFn.cancel();
			}
		};
	}, [debouncedOnInit]);

	useEffect(() => {
		const messageHandler = async (message: MessageEvent) => {
			const iframe = iframeRef.current?.contentWindow;
			if (!iframe) {
				return;
			}

			const eventName = message?.data?.event;
			const event = createAnalyticsEvent({});

			switch (eventName) {
				case JIRA_EMBED_ISSUE_CREATE_READY:
					fireOperationalAnalytics(event, 'globalIssueCreateEmbed initialised', {
						consumer,
					});

					setIsGicReady(true);

					break;
				case JIRA_EMBED_ISSUE_CREATE_SUCCESS:
					setIsCreateSuccess(true);
					onSuccess(message.data.payload);
					fireTrackAnalytics(event, 'globalIssueCreateEmbed succeeded', {
						consumer,
					});
					break;
				case JIRA_EMBED_ISSUE_CREATE_FAILURE:
					onFailure(new Error('issueCreate failed to save'));
					fireOperationalAnalytics(event, 'globalIssueCreateEmbed failed', {
						consumer,
					});
					break;
				case JIRA_EMBED_ISSUE_CREATE_CLOSE:
					onClose(isCreateSuccess);
					// GIC sends a closeEvent even when created succesfully. Hence
					// checking if no issue was created
					if (!isCreateSuccess) {
						fireUIAnalytics(event, 'globalIssueCreateEmbed closed', {
							consumer,
						});
					}
					break;

				default:
				// We don't handle other events
			}
		};

		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		window.addEventListener('message', messageHandler);

		return () => {
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			window.removeEventListener('message', messageHandler);
		};
	}, [
		iframeRef,
		consumer,
		createAnalyticsEvent,
		onInit,
		onClose,
		onFailure,
		onSuccess,
		isCreateSuccess,
		setIsCreateSuccess,
		setIsGicReady,
	]);

	useEffect(() => {
		if (isPayloadReady && isGicReady) {
			// Only resolve the Promise if isPayloadReady is true, and the message has been received
			Promise.resolve(onGICReadyRef.current()).then((issuePayload) => {
				const iframe = iframeRef.current?.contentWindow;
				if (iframe) {
					const { fieldData, ...defaultValues } = issuePayload ?? {};
					// Send GIC data used for initialization
					iframe.postMessage(
						{
							event: JIRA_EMBED_ISSUE_CREATE_INIT,
							payload: {
								defaultValues,
								...(fieldData && { fieldData: { ...fieldData, ...defaultValues } }),
							},
						},
						// Broadcast this message to all listeners for now
						'*',
					);
					debouncedOnInit?.();
				}
			});
		}
	}, [isPayloadReady, isGicReady, onGICReadyRef, debouncedOnInit]);

	const iframeSrc = getIframeSrc({
		colorMode,
		domainName,
		params,
		displaySitePicker,
		...configuration,
		overrideCloudId,
	});

	useEffect(() => {
		isGicReadyRef.current = isGicReady;
	}, [isGicReady]);

	useEffect(() => {
		// We need to reset the isGicReady state to avoid sending another postMessage to the GIC iframe window
		setIsGicReady(false);
	}, [iframeSrc]);

	useEffect(() => {
		const TIMEOUT_10_SECONDS = 10 * 1000;
		const timeout = setTimeout(() => {
			// We need to use a ref to check if GIC is ready because the isGicReady state is updated asynchronously
			if (!isGicReadyRef.current) {
				// This is a fallback for the case when the GIC iframe window is not initialized
				throw new Error('jira-ai/gic-anywhere: The latest GIC iframe window was not initialized');
			}
		}, TIMEOUT_10_SECONDS);

		return () => clearTimeout(timeout);
	}, [iframeSrc]);

	return (
		<IFrame
			name="gic-anywhere-iframe"
			sandbox={undefined}
			ref={iframeRef}
			height="100%"
			width="100%"
			frameBorder={0}
			// iframes do not fire onError due to security reasons
			// onError={}
			src={iframeSrc}
			data-testid="gic-anywhere"
		/>
	);
};

/**
 * iframe element isolated for DI
 */
export const IFrame = forwardRef<HTMLIFrameElement, React.ComponentProps<'iframe'>>(
	(props, ref) => {
		return <iframe title="Jira Embed Global Issue Create" ref={ref} {...props} />;
	},
);
